import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { FiSave, FiX } from 'react-icons/fi';
import { saveQuoteRevision } from '../../api/revision-api';
import { getCellDiff, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly } from './utils';
import { QuoteTableEdit } from './QuoteTableEdit';
import type { CellEditValues } from './QuoteTableEdit';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import type { QuoteRevisionSnapshot, SaveQuoteCellPayload } from '../../types/revision.type';
import type { RfxQuoteTable } from '../../types/quote.type';
import type { QuoteTableColumn } from '../../../components/QuoteTable/quote-table.types';
import { Modal } from 'newui/components/ui/Modal';
import { Button } from 'newui/components/Button';

export interface UpdateQuoteModalProps {
    currentData: QuoteRevisionSnapshot;
    inviteId: number;
    isOpen: boolean;
    previousData: QuoteRevisionSnapshot;
    quote: RfxQuoteTable;
    onClose: () => void;
    onSaveSuccess: (isQuoteTabValid?: boolean) => void;
}

const buildInitialCellEditValues = (data: QuoteRevisionSnapshot): CellEditValues => {
    const initial: CellEditValues = {};
    if (!data?.cells) {
        return initial;
    }
    data.cells.forEach((cell) => {
        if (!initial[cell.rfx_quote_row_id]) {
            initial[cell.rfx_quote_row_id] = {};
        }
        initial[cell.rfx_quote_row_id][cell.rfx_quote_column_id] = cell.value ?? '';
    });
    return initial;
};

const mapColumnsToTableColumns = (quote: RfxQuoteTable): QuoteTableColumn[] => {
    if (!quote.columns || quote.columns.length === 0) {
        return [];
    }
    return quote.columns.map((col) => ({
        id: col.id,
        columnType: col.columnType,
        label: col.name,
        sortOrder: col.sortOrder,
        isSupplierEditable: col.isSupplierEditable,
    }));
};

const UpdateQuoteModal: React.FC<UpdateQuoteModalProps> = ({
    isOpen,
    onClose,
    inviteId,
    currentData,
    previousData,
    onSaveSuccess,
    quote,
}) => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const tableColumns = useMemo(() => mapColumnsToTableColumns(quote), [quote]);

    const [editCellValues, setEditCellValues] = useState<CellEditValues>(
        () => buildInitialCellEditValues(currentData)
    );

    const diffKeys = useMemo(() => {
        const base = getCellDiff(editCellValues, buildInitialCellEditValues(previousData));
        // The `total` column is computed, not stored, so cell-level diffing
        // never flags it. Manually add a diff key for the total column on any
        // row that has at least one input-cell diff (quantity / rate / saving),
        // so both the editable and read-only sides highlight the recomputed total.
        const totalCol = quote.columns?.find((c) => c.columnType === 'total');
        if (!totalCol) {
            return base;
        }
        const inputColIds = new Set(
            (quote.columns ?? [])
                .filter((c) =>
                    c.columnType === 'response_quantity' ||
                    c.columnType === 'response_value' ||
                    c.columnType === 'saving'
                )
                .map((c) => c.id)
        );
        const augmented = new Set(base);
        for (const key of base) {
            const match = key.match(/^cell-(\d+)-(\d+)$/);
            if (!match) continue;
            const rowId = Number(match[1]);
            const colId = Number(match[2]);
            if (inputColIds.has(colId)) {
                augmented.add(`cell-${rowId}-${totalCol.id}`);
            }
        }
        return augmented;
    }, [editCellValues, previousData, quote.columns]);

    const currentRows = useMemo(
        () => mapCellSnapshotToRows(editCellValues, quote.rows, quote.columns),
        [editCellValues, quote.rows, quote.columns]
    );

    const previousRows = useMemo(
        () => mapCellSnapshotToRows(buildInitialCellEditValues(previousData), quote.rows, quote.columns),
        [previousData, quote.rows, quote.columns]
    );

    const readOnlyPreviousRows = useMemo(
        () => mapQuoteTableRowsToReadOnly(previousRows, tableColumns),
        [previousRows, tableColumns]
    );

    const hasChanges = diffKeys.size > 0;

    const handleCellValueChange = useCallback(
        (rowId: number, columnId: number, value: string) => {
            setEditCellValues((prev) => ({
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    [columnId]: value,
                },
            }));
        },
        []
    );

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const cells: SaveQuoteCellPayload[] = [];
            Object.entries(editCellValues).forEach(([rowId, columnMap]) => {
                Object.entries(columnMap).forEach(([columnId, val]) => {
                    cells.push({
                        row_id: Number(rowId),
                        column_id: Number(columnId),
                        value: String(val) || null,
                    });
                });
            });
            const result = await saveQuoteRevision(
                { inviteId: inviteId, quoteId: quote.id },
                { cells }
            );
            toast({
                title: 'Revision saved',
                description: 'Your quote revision has been saved.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            onSaveSuccess(result.isQuoteTabValid);
            onClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to save quote revision.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditCellValues(buildInitialCellEditValues(currentData));
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            size="full"
            title="Update Quote"
            modalType='standard'
            iconType='edit'
            customFooter={<div className='flex justify-end gap-4'>
                <Button
                    btntype='secondary'
                    onClick={handleCancel}
                    disabled={isLoading}
                    data-testid="cancel-update-btn"
                >
                    <FiX /> <span className='pl-2'>Cancel Changes</span>
                </Button>
                <Button
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={!hasChanges || isLoading}
                    data-testid="save-revision-btn"
                >
                    <FiSave /> <span className='pl-2'>Save Revision</span>
                </Button></div>}
        >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="mb-6">
                    <p className="mb-3 text-base font-semibold text-gray-700">
                        Current Revision (Editable) - {currentData.label}
                    </p>
                    <QuoteTableEdit
                        rows={currentRows}
                        columns={tableColumns}
                        editCellValues={editCellValues}
                        onCellValueChange={handleCellValueChange}
                        diffKeys={diffKeys}
                        showUnitColumn={false}
                        showSubTotal={false}
                    />
                </div>

                <div>
                    <p className="mb-3 text-base font-semibold text-gray-500">
                        Previous Revision (Read-only) - {previousData.label}
                    </p>
                    <ReadOnlyQuoteTable
                        columns={tableColumns}
                        rows={readOnlyPreviousRows}
                        muted
                    />
                </div>
            </div>
        </Modal>
    );
};

export { UpdateQuoteModal };
